import format from 'utils/format'
import { getDeepProperty } from 'utils/getDeepProperty'

export type PartnerService = 'callback' | 'callbackConfig' | 'listage' | 'link' | 'preApproval' | 'sendForm' | 'signature'
export type PartnerCallbackMethod = 'get' | 'post' | 'patch'
export type PartnerCallbackAuthType = 'basic' | 'bearer'

export interface IPartnerCallbackConfiguration {
  url?: string
  token?: string
  method?: PartnerCallbackMethod
  authType?: PartnerCallbackAuthType
  responseConfig?: Array<'body' | 'query'>
  useProxy?: boolean
  typeSpecific?: Array<{ callbackType: string, url?: string, method?: PartnerCallbackMethod }>
  triggers?: {
    status?: string[]
    department?: string[]
    step?: string[]
  }
}

export interface IPartnerListItem {
  _id: string
  name: string
  email: string
  cnpj?: string
  partner?: boolean
  hasToken: boolean
  servicesCount: number
  blocked?: boolean
  canceled?: boolean
}

export interface IPartnerDetail extends Omit<IPartnerListItem, 'hasToken' | 'servicesCount'> {
  token?: string
  services?: PartnerService[]
  callbackConfiguration?: IPartnerCallbackConfiguration
  callbackFields?: Record<string, unknown>
}

export type PartnerUpdateBody = Partial<{
  partner: boolean
  token: string
  services: PartnerService[]
  callbackConfiguration: IPartnerCallbackConfiguration
  callbackFields: Record<string, unknown> | ''
}>

export interface IPartnerLog {
  _id: string
  createdAt: string
  modifiedBy?: { name: string }
  modifiedFields: string[]
  last?: Record<string, unknown>
  new?: Record<string, unknown>
}

export const partnerServices: Array<{ value: PartnerService, label: string }> = [
  { value: 'callback', label: 'Callback (callback)' },
  { value: 'callbackConfig', label: 'Configuração de callback (callbackConfig)' },
  { value: 'listage', label: 'Listagem de propostas (listage)' },
  { value: 'link', label: 'Link de formulário (link)' },
  { value: 'preApproval', label: 'Pré-aprovação (preApproval)' },
  { value: 'sendForm', label: 'Envio de formulário (sendForm)' },
  { value: 'signature', label: 'Assinatura (signature)' }
]

export const statusTriggers = ['created', 'approved', 'reproved', 'formsFinished']
export const resultTriggers = ['approved', 'reproved']
export const callbackTypes = ['proposalCreated', 'finishedForms', 'proposalApproved', 'proposalReproved', 'starting', 'remaking', 'analysis', 'reproved', 'approved', 'reservation', 'ongoing']

export function optionsWithSaved (base: string[], saved: string[] = []) {
  return [...base, ...saved.filter(value => !base.includes(value))].map(value => ({ label: value, value }))
}

export function unsupportedTriggers (triggers?: IPartnerCallbackConfiguration['triggers']): string[] {
  const invalid = [
    ...(triggers?.status ?? []).filter(value => !statusTriggers.includes(value)),
    ...(triggers?.department ?? []).filter(value => !resultTriggers.includes(value)),
    ...(triggers?.step ?? []).filter(value => !resultTriggers.includes(value))
  ]

  return Array.from(new Set(invalid))
}

export function partnerTags (promoter: Pick<IPartnerListItem, 'partner' | 'hasToken' | 'blocked' | 'canceled'>): string[] {
  const tags = [promoter.partner ? 'Habilitado' : 'Desabilitado']

  if (promoter.blocked) tags.push('Bloqueado')
  if (promoter.partner && !promoter.hasToken) tags.push('Sem token')
  if (promoter.canceled) tags.push('Cancelado')

  return tags
}

function jsonError (text: string): { index: number, reason: string } | undefined {
  let i = 0
  const fail = (reason: string): never => { throw Object.assign(new Error(reason), { index: i }) }
  const skip = () => { while (/[ \t\n\r]/.test(text[i] ?? '')) i++ }
  const literal = (word: string) => {
    if (!text.startsWith(word, i)) fail(`esperado ${word}`)
    i += word.length
  }
  const string = () => {
    i++
    while (text[i] !== '"') {
      if (i >= text.length) fail('texto entre aspas não foi fechado')
      if (text[i] < ' ') fail('caractere de controle dentro do texto (para quebra de linha use \\n)')
      if (text[i] === '\\') {
        i++
        if (text[i] === 'u') {
          if (!/^[0-9a-fA-F]{4}$/.test(text.slice(i + 1, i + 5))) fail('código \\u inválido')
          i += 4
        } else if (text[i] === undefined || !'"\\/bfnrt'.includes(text[i])) {
          i--
          fail(`barra invertida inválida (\\${text[i + 1] ?? ''})`)
        }
      }
      i++
    }
    i++
  }
  const number = () => {
    const pattern = /-?(0|[1-9]\d*)(\.\d+)?([eE][+-]?\d+)?/y
    pattern.lastIndex = i
    const match = pattern.exec(text)
    if (!match) fail('valor inválido')
    i += match![0].length
  }
  const value = (): void => {
    skip()
    const char = text[i]

    if (char === '{') {
      i++
      skip()
      if (text[i] === '}') { i++; return }
      for (;;) {
        skip()
        if (text[i] !== '"') fail('esperado nome do campo entre aspas')
        string()
        skip()
        if (text[i] !== ':') fail('esperado ":"')
        i++
        value()
        skip()
        if (text[i] === ',') { i++; continue }
        if (text[i] === '}') { i++; return }
        fail('esperado "," ou "}"')
      }
    } else if (char === '[') {
      i++
      skip()
      if (text[i] === ']') { i++; return }
      for (;;) {
        value()
        skip()
        if (text[i] === ',') { i++; continue }
        if (text[i] === ']') { i++; return }
        fail('esperado "," ou "]"')
      }
    } else if (char === '"') string()
    else if (char === 't') literal('true')
    else if (char === 'f') literal('false')
    else if (char === 'n') literal('null')
    else number()
  }

  try {
    value()
    skip()
    if (i < text.length) fail('conteúdo depois do fim do JSON')
  } catch (err) {
    const found = err as Error & { index?: number }

    if (found.index !== undefined) return { index: found.index, reason: found.message }
    throw err
  }
}

export function parseJsonWithLine (text: string): { value?: Record<string, unknown>, error?: string } {
  if (!text.trim()) return {}

  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch (err) {
    const found = jsonError(text)

    if (!found) return { error: 'JSON inválido' }
    const before = text.slice(0, found.index)
    const line = before.split('\n').length
    const column = found.index - before.lastIndexOf('\n')

    return { error: `JSON inválido na linha ${line}, coluna ${column}: ${found.reason}` }
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { error: 'JSON inválido: deve ser um objeto JSON' }
  }

  return { value: parsed as Record<string, unknown> }
}

function valueAt (source: Record<string, unknown> | undefined, path: string) {
  if (!source) return undefined
  if (path in source) return source[path]

  return getDeepProperty(source, path.split('.'))
}

export function describeLog (log: IPartnerLog): string[] {
  return log.modifiedFields.map(field => {
    const before = valueAt(log.last, field)
    const after = valueAt(log.new, field)

    if (field === 'partner') return after ? 'Parceiro habilitado' : 'Parceiro desabilitado'
    if (field === 'token') return 'Token rotacionado'
    if (field === 'callbackFields') return 'Campos do callback alterados'

    if (field === 'services') {
      const previous = (before ?? []) as string[]
      const current = (after ?? []) as string[]
      const changes = [
        ...current.filter(service => !previous.includes(service)).map(service => `+${service}`),
        ...previous.filter(service => !current.includes(service)).map(service => `-${service}`)
      ]

      return changes.length ? `Serviços alterados: ${changes.join(', ')}` : 'Serviços alterados'
    }

    if (field === 'callbackConfiguration.method') {
      return `Callback: método ${String(before ?? '-').toUpperCase()} → ${String(after ?? '-').toUpperCase()}`
    }

    if (field === 'callbackConfiguration.authType') {
      return `Callback: autenticação ${format.capitalize(String(before ?? '-'))} → ${format.capitalize(String(after ?? '-'))}`
    }

    if (field.startsWith('callbackConfiguration.')) {
      return `Callback: ${field.replace('callbackConfiguration.', '')} alterado`
    }

    return `${field} alterado`
  })
}
